import * as Popover from '@radix-ui/react-popover';
import type { PartnerOrganization } from '@reach/shared-types/workos-auth';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import React, { useState } from 'react';
import { OrganizationLogo } from './OrganizationLogo';
import { OrganizationOption } from './OrganizationOption';
import styles from './OrgSwitcherGroup.module.css';
import { useAuth } from '../hooks/useAuth';
import { partnerApi, type OrganizationsResponse, type Partner } from '../lib/api';
import { forgetActiveGeneration } from '../pages/feature-setup/automations-tab/reputation-defaults-menu/activeGenerationStorage';
import { clearAiSegmentDraft } from '../pages/feature-setup/segments-tab/describe-segment-modal/types';

interface OrgSwitcherProps {
  partner: Partner;
  compact: boolean;
}

export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ partner, compact }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [switchingToOrgId, setSwitchingToOrgId] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const { user, organization } = useAuth();

  const { data: organizationsResponse, isLoading } = useQuery<OrganizationsResponse>({
    queryKey: ['organizations'],
    queryFn: () => partnerApi.getOrganizations({ limit: 100 }),
    enabled: !!user,
  });

  const organizationsPayload = organizationsResponse?.data as
    | { results: PartnerOrganization[] }
    | undefined;
  const organizations = organizationsPayload?.results || [];
  const showSwitcher = organizations.length > 1;

  const query = searchQuery.trim().toLowerCase();
  const filteredOrgs = organizations.filter(
    org => org.name.toLowerCase().includes(query) || org.domain?.toLowerCase().includes(query),
  );

  const handleSwitch = async (orgId: string) => {
    if (orgId === organization?.id) {
      setIsOpen(false);
      return;
    }

    // Drafts are scoped to the organization that generated them; the reauth
    // redirect below keeps sessionStorage, so drop it before leaving.
    clearAiSegmentDraft();
    forgetActiveGeneration();

    setSwitchingToOrgId(orgId);
    try {
      const switchResponse = await partnerApi.switchOrganization(orgId);
      if (switchResponse.data.requiresReauth) {
        window.location.href = switchResponse.data.authorizationUrl;
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      setSwitchingToOrgId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, filteredOrgs.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const org = filteredOrgs[focusedIndex];
      if (org) handleSwitch(org.id);
    }
  };

  const organizationName = organization?.name || partner.name;
  const triggerLabel = `Change organization: ${organizationName}`;
  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
        if (!open) {
          setSearchQuery('');
          setFocusedIndex(0);
        }
      }}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.trigger}
          data-compact={compact}
          title={organizationName}
          disabled={!showSwitcher}
          aria-label={triggerLabel}>
          <OrganizationLogo name={partner.name} logoUrl={partner.logoUrl} />
          <span>{organizationName}</span>
          {showSwitcher && <ChevronDown />}
        </button>
      </Popover.Trigger>
      {isOpen && (
        <Popover.Portal>
          <Popover.Content
            className={styles.popover}
            side="bottom"
            align="start"
            sideOffset={8}
            onKeyDown={handleKeyDown}>
            <label className={styles.search}>
              <Search aria-hidden />
              <input
                type="search"
                aria-label="Search organizations"
                placeholder="Search organizations…"
                value={searchQuery}
                onChange={event => {
                  setSearchQuery(event.target.value);
                  setFocusedIndex(0);
                }}
              />
            </label>
            {isLoading ? (
              <p className={styles.message}>
                <Loader2 className={styles.spinner} />
                Loading…
              </p>
            ) : filteredOrgs.length === 0 ? (
              <p className={styles.message}>No organizations found</p>
            ) : (
              <div className={styles.options}>
                {filteredOrgs.map((org, index) => (
                  <OrganizationOption
                    key={org.id}
                    organization={org}
                    currentId={organization?.id}
                    switchingToId={switchingToOrgId}
                    focused={index === focusedIndex}
                    onSelect={handleSwitch}
                    onFocus={() => setFocusedIndex(index)}
                  />
                ))}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      )}
    </Popover.Root>
  );
};
